import { useState, useEffect, useRef, useCallback } from "react";

// ─────────────────────────────────────────────────────────────────────────────
//  SYSTÈME POI — BULLETPROOF
//  Stratégie :
//   1. UNE SEULE requête Overpass compacte (pas de regex, pas de way)
//   2. RACE entre 4 serveurs en parallèle → le premier qui répond gagne
//   3. Cache mémoire LRU (clé = cellule 0.05° ≈ 3km) — évite les refetch
//   4. Retry automatique × 2 par serveur avec backoff exponentiel
//   5. Timeout individuel par tentative (15s) + timeout global (45s)
//   6. Validation stricte JSON + détection HTML d'erreur
//   7. onProgress callback pour afficher l'état en temps réel dans l'UI
// ─────────────────────────────────────────────────────────────────────────────

// Rayon du couloir autour du trajet (mètres)
const CORRIDOR_RADIUS = 1000;

// 4 serveurs Overpass publics — on les interroge tous en même temps
const OVERPASS_SERVERS = [
  "https://overpass-api.de/api/interpreter",
  "https://overpass.kumi.systems/api/interpreter",
  "https://overpass.openstreetmap.ru/api/interpreter",
];

// Requête autour d'un point intermédiaire du trajet
const buildQuery = (lat, lon, r) => `[out:json][timeout:15];(
  // Nature & Relief
  node["natural"~"peak|cliff|cave_entrance|rock|stone|tree|spring|waterfall"](around:${r},${lat},${lon});
  
  // Tourisme & Patrimoine
  node["tourism"~"viewpoint|picnic_site|camp_site|information|artwork|museum|attraction"](around:${r},${lat},${lon});
  node["historic"~"memorial|monument|ruins|castle|archaeological_site|wayside_cross|wayside_shrine"](around:${r},${lat},${lon});
  
  // Equipements & Urbain (pour ne jamais être à sec de points)
  node["amenity"~"drinking_water|bench|shelter|fountain|place_of_worship"](around:${r},${lat},${lon});
  node["leisure"~"nature_reserve|park|garden|bird_hide"](around:${r},${lat},${lon});
  
  // Man_made (curiosités)
  node["man_made"~"lighthouse|tower|windmill|water_tower|cross"](around:${r},${lat},${lon});
);out body;`;

// ── Génère N points intermédiaires le long du trajet (userPos → target) ──────
// Retourne un tableau de {lat, lon} incluant les deux extrémités
function interpolateRoute(p1, p2, steps = 5) {
  const pts = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    pts.push({ lat: p1.lat + (p2.lat - p1.lat) * t, lon: p1.lon + (p2.lon - p1.lon) * t });
  }
  return pts;
}

// ── Distance perpendiculaire d'un point P au segment AB (en mètres) ──────────
function distPointToSegment(pLat, pLon, aLat, aLon, bLat, bLon) {
  // On travaille en mètres approximatifs (projection plate locale)
  const cos = Math.cos((aLat * Math.PI) / 180);
  const ax = 0, ay = 0;
  const bx = (bLon - aLon) * 111320 * cos, by = (bLat - aLat) * 111320;
  const px = (pLon - aLon) * 111320 * cos, py = (pLat - aLat) * 111320;
  const len2 = bx * bx + by * by;
  if (len2 === 0) return Math.sqrt(px * px + py * py);
  let t = (px * bx + py * by) / len2;
  t = Math.max(0, Math.min(1, t));
  const dx = px - t * bx, dy = py - t * by;
  return Math.sqrt(dx * dx + dy * dy);
}

// ── Cache LRU simple (max 20 entrées) ────────────────────────────────────────
const _poiCache = new Map(); // clé → { elements, ts }
const POI_CACHE_TTL = 10 * 60 * 1000; // 10 min
const POI_CACHE_MAX = 20;
function cacheKey(lat, lon) {
  // Cellule ~1km × 1km (plus fine pour le couloir)
  return `${(lat / 0.009).toFixed(0)}_${(lon / 0.012).toFixed(0)}`;
}
function cacheGet(lat, lon) {
  const k = cacheKey(lat, lon);
  const entry = _poiCache.get(k);
  if (!entry) return null;
  if (Date.now() - entry.ts > POI_CACHE_TTL) { _poiCache.delete(k); return null; }
  return entry.elements;
}
function cacheSet(lat, lon, elements) {
  const k = cacheKey(lat, lon);
  if (_poiCache.size >= POI_CACHE_MAX) {
    _poiCache.delete(_poiCache.keys().next().value);
  }
  _poiCache.set(k, { elements, ts: Date.now() });
}

// ── Fetch atomique : 1 requête, 1 serveur, timeout strict ────────────────────
async function fetchOnce(server, query, timeoutMs = 15000) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(new Error("timeout")), timeoutMs);
  try {
    const res = await fetch(server, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: "data=" + encodeURIComponent(query),
      signal: ctrl.signal,
    });
    // Tout code >= 400 → erreur → serveur suivant
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const text = await res.text();
    // Détection HTML d'erreur (serveur Apache/nginx qui renvoie une page)
    if (text.trimStart().startsWith("<")) throw new Error("Réponse HTML inattendue");
    let data;
    try { data = JSON.parse(text); } catch { throw new Error("JSON invalide"); }
    if (!Array.isArray(data.elements)) throw new Error("Champ elements manquant");
    return data.elements;
  } finally {
    clearTimeout(timer);
  }
}

// ── Fetch avec retry exponentiel sur UN serveur ───────────────────────────────
async function fetchWithRetry(server, query, maxRetries = 2) {
  let delay = 600;
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fetchOnce(server, query);
    } catch (e) {
      const isLast = i === maxRetries - 1;
      console.warn(`[Overpass] ${server} tentative ${i + 1}/${maxRetries}: ${e.message}`);
      if (isLast) throw e;
      await new Promise(r => setTimeout(r, delay));
      delay *= 2; // backoff : 600ms → 1200ms
    }
  }
}

// ── RACE entre tous les serveurs ─────────────────────────────────────────────
// Le premier serveur à répondre correctement gagne.
// Les autres sont annulés via AbortController partagé.
// Timeout global de 45s en dernier recours.
// ── Fetch un seul point intermédiaire (avec cache) ───────────────────────────
async function fetchOnePoint(lat, lon) {
  const cached = cacheGet(lat, lon);
  if (cached) return cached;
  const query = buildQuery(lat, lon, CORRIDOR_RADIUS);
  return new Promise((resolve, reject) => {
    let settled = false;
    let pending = OVERPASS_SERVERS.length;
    const errors = [];
    const globalTimer = setTimeout(() => {
      if (!settled) { settled = true; reject(new Error("Timeout (45s)")); }
    }, 45000);
    OVERPASS_SERVERS.forEach(server => {
      fetchWithRetry(server, query)
        .then(elements => {
          if (settled) return;
          settled = true;
          clearTimeout(globalTimer);
          cacheSet(lat, lon, elements);
          console.info(`[Overpass] ✓ ${server} (${elements.length} éléments)`);
          resolve(elements);
        })
        .catch(e => {
          errors.push(`${server}: ${e.message}`);
          pending--;
          if (pending === 0 && !settled) {
            settled = true;
            clearTimeout(globalTimer);
            reject(new Error("Tous les serveurs sont indisponibles."));
          }
        });
    });
  });
}

// ── Fetch POIs le long du couloir userPos → target ────────────────────────────
// Stratégie : on interroge N points intermédiaires, on fusionne les résultats,
// on déduplique par id OSM, puis on filtre par distance perpendiculaire au trajet.
async function fetchPOIs(userPos, targetPos, onProgress) {
  onProgress?.("fetching");

  // Nombre de points selon la distance (plus loin = plus de points)
  const totalDist = Math.sqrt(
    Math.pow((targetPos.lat - userPos.lat) * 111320, 2) +
    Math.pow((targetPos.lon - userPos.lon) * 111320 * Math.cos(userPos.lat * Math.PI / 180), 2)
  );
  const steps = Math.max(3, Math.min(8, Math.ceil(totalDist / 800)));
  const waypoints = interpolateRoute(userPos, targetPos, steps);

  // Lance toutes les requêtes en parallèle
  const results = await Promise.allSettled(waypoints.map(wp => fetchOnePoint(wp.lat, wp.lon)));

  // Fusionne et déduplique
  const seen = new Set();
  const all = [];
  for (const r of results) {
    if (r.status === "fulfilled") {
      for (const el of r.value) {
        if (!seen.has(el.id)) { seen.add(el.id); all.push(el); }
      }
    }
  }

  if (all.length === 0 && results.every(r => r.status === "rejected")) {
    throw new Error("Tous les serveurs sont indisponibles. Réessaie dans quelques secondes.");
  }

  onProgress?.("done");
  return all;
}

const POI_ICONS = {
  rock: "🪨", stone: "🪨", boulder: "🪨", arch: "🌉",
  cave_entrance: "🕳️", peak: "⛰️", hill: "🏔️", cliff: "🧗",
  waterfall: "💧", spring: "💦", geyser: "♨️", sinkhole: "🕳️",
  hot_spring: "♨️", tree: "🌳", viewpoint: "🔭", attraction: "⭐",
  artwork: "🎨", megalith: "🗿", standing_stone: "🗿", dolmen: "🗿",
  archaeological_site: "🏺", geological: "🧱",
};
const POI_LABELS = {
  rock: "Rocher", stone: "Pierre", boulder: "Bloc rocheux",
  arch: "Arche naturelle", cave_entrance: "Entrée de grotte",
  peak: "Sommet", hill: "Colline", cliff: "Falaise",
  waterfall: "Cascade", spring: "Source", geyser: "Geyser",
  sinkhole: "Gouffre", hot_spring: "Source chaude",
  tree: "Arbre remarquable", viewpoint: "Point de vue",
  attraction: "Curiosité", artwork: "Œuvre d'art",
  megalith: "Mégalithe", standing_stone: "Menhir", dolmen: "Dolmen",
  archaeological_site: "Site archéologique", geological: "Formation géologique",
};

const PRESET_CITIES = [
  { name: "Paris",     emoji: "🗼", lat: 48.8566,  lon: 2.3522   },
  { name: "Montréal",  emoji: "🍁", lat: 45.5017,  lon: -73.5673 },
  { name: "Lyon",      emoji: "🦁", lat: 45.764,   lon: 4.8357   },
  { name: "Tokyo",     emoji: "🗾", lat: 35.6762,  lon: 139.6503 },
  { name: "New York",  emoji: "🗽", lat: 40.7128,  lon: -74.006  },
  { name: "Londres",   emoji: "🎡", lat: 51.5074,  lon: -0.1278  },
  { name: "Barcelone", emoji: "🏖️", lat: 41.3851,  lon: 2.1734   },
  { name: "Rome",      emoji: "🏛️", lat: 41.9028,  lon: 12.4964  },
];

// ─── Thème sombre (harmonisé avec index.html #060610) ──────────────────────
const C = {
  bg:        "#060610",
  bgCard:    "#0d1020",
  bgSoft:    "#12172a",
  bgMid:     "#1a2240",
  border:    "#1e2d55",
  borderMid: "#263565",
  blue:      "#3b82f6",
  blueDark:  "#2563eb",
  blueLight: "#1e2d55",
  blueMid:   "#2a4080",
  accent:    "#60a5fa",
  accentBg:  "#0f1e40",
  red:       "#ef4444",
  redBg:     "#1a0a0a",
  redMid:    "#7f1d1d",
  green:     "#34d399",
  greenBg:   "#022c22",
  text:      "#e2eaf5",
  textMid:   "#8aa4cc",
  textLight: "#4a6080",
  shadow:    "0 2px 20px rgba(0,0,0,0.5)",
  shadowMd:  "0 4px 32px rgba(0,0,0,0.6)",
};

// ─── Curseur distance ──────────────────────────────────────────────────────
// Valeurs snap : 500m, 1km, 2km, 3.5km, 5km
// On encode le curseur comme index 0-4
const DISTANCE_STEPS = [500, 1000, 2000, 3500, 5000];
const DISTANCE_LABELS = ["500 m", "1 km", "2 km", "3.5 km", "5 km"];
const DISTANCE_EMOJIS = ["🐾", "🚶", "🚴", "🏃", "🌲"];

// Couloir de 1km de chaque côté du trajet

// ─── Utilitaires ──────────────────────────────────────────────────────────
function getPOIType(node) {
  const t = node.tags || {};
  return t.natural || t.tourism || t.historic || t.geological || null;
}
function getPOIIcon(node) { return POI_ICONS[getPOIType(node)] || "🪨"; }
function getPOILabel(node) { return POI_LABELS[getPOIType(node)] || getPOIType(node) || "Curiosité naturelle"; }
function getPOIName(node) { const t = node.tags || {}; return t.name || t["name:fr"] || null; }

function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000, phi1 = (lat1 * Math.PI) / 180, phi2 = (lat2 * Math.PI) / 180;
  const dphi = ((lat2 - lat1) * Math.PI) / 180, dl = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dphi / 2) ** 2 + Math.cos(phi1) * Math.cos(phi2) * Math.sin(dl / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
function getBearing(lat1, lon1, lat2, lon2) {
  const phi1 = (lat1 * Math.PI) / 180, phi2 = (lat2 * Math.PI) / 180, dl = ((lon2 - lon1) * Math.PI) / 180;
  const y = Math.sin(dl) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dl);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}
function generateRandomPoint(lat, lon, targetDist) {
  // Distance exacte ± 20%
  const minR = targetDist * 0.8, maxR = targetDist * 1.2;
  const r = minR + Math.random() * (maxR - minR), a = Math.random() * 2 * Math.PI;
  return {
    lat: lat + (r * Math.cos(a)) / 111320,
    lon: lon + (r * Math.sin(a)) / (111320 * Math.cos((lat * Math.PI) / 180)),
  };
}
const formatDist = (m) => m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`;

// ─── Mini carte canvas ────────────────────────────────────────────────────
const W = 440, H = 220;
function latLonToTile(lat, lon, z) {
  const x = Math.floor(((lon + 180) / 360) * Math.pow(2, z));
  const sinLat = Math.sin((lat * Math.PI) / 180);
  const y = Math.floor((0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * Math.pow(2, z));
  return { x, y };
}
function latLonToPixel(lat, lon, cLat, cLon, z) {
  const sc = Math.pow(2, z) * 256;
  const toX = (lng) => ((lng + 180) / 360) * sc;
  const toY = (la) => { const s = Math.sin((la * Math.PI) / 180); return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * sc; };
  return { px: W / 2 + (toX(lon) - toX(cLon)), py: H / 2 + (toY(lat) - toY(cLat)) };
}

function MiniMap({ userPos, target, onMapClick, heading }) {
  const canvasRef = useRef(null);
  const tilesRef = useRef({});
  const [tick, setTick] = useState(0);
  const [zoom, setZoom] = useState(13);
  const pinchRef = useRef(null);
  const cLat = userPos?.lat ?? 48.8566;
  const cLon = userPos?.lon ?? 2.3522;

  // ── Dessin bonhomme orienté ───────────────────────────────────────────────
  function drawPerson(ctx, cx, cy, angleDeg) {
    const rad = (angleDeg * Math.PI) / 180;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rad);

    // Halo de position
    ctx.beginPath(); ctx.arc(0, 0, 17, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(59,130,246,0.18)"; ctx.fill();

    // Cône de vision (devant = haut avant rotation)
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.arc(0, 0, 22, -Math.PI * 0.28, Math.PI * 0.28);
    ctx.closePath();
    ctx.fillStyle = "rgba(59,130,246,0.22)"; ctx.fill();

    // Corps (ellipse)
    ctx.beginPath(); ctx.ellipse(0, 3, 4, 5.5, 0, 0, Math.PI * 2);
    ctx.fillStyle = "#3b82f6"; ctx.fill();

    // Tête
    ctx.beginPath(); ctx.arc(0, -5, 4.5, 0, Math.PI * 2);
    ctx.fillStyle = "#60a5fa"; ctx.fill();
    ctx.beginPath(); ctx.arc(0, -5, 4.5, 0, Math.PI * 2);
    ctx.strokeStyle = "#fff"; ctx.lineWidth = 1.2; ctx.stroke();

    // Flèche de direction (au-dessus de la tête)
    ctx.beginPath();
    ctx.moveTo(0, -11);
    ctx.lineTo(-3.5, -16);
    ctx.lineTo(0, -14);
    ctx.lineTo(3.5, -16);
    ctx.closePath();
    ctx.fillStyle = "#fff"; ctx.fill();

    ctx.restore();
  }

  const draw = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = C.bgSoft; ctx.fillRect(0, 0, W, H);
    const ct = latLonToTile(cLat, cLon, zoom);
    for (let dx = -4; dx <= 4; dx++) { for (let dy = -3; dy <= 3; dy++) {
      const tx = ct.x + dx, ty = ct.y + dy, key = `${zoom}/${tx}/${ty}`;
      const n = Math.PI - (2 * Math.PI * ty) / Math.pow(2, zoom);
      const tLat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
      const tLon = (tx / Math.pow(2, zoom)) * 360 - 180;
      const { px, py } = latLonToPixel(tLat, tLon, cLat, cLon, zoom);
      if (tilesRef.current[key] && tilesRef.current[key] !== "loading") {
        ctx.globalAlpha = 0.85;
        ctx.drawImage(tilesRef.current[key], Math.round(px), Math.round(py), 256, 256);
        ctx.globalAlpha = 1;
      } else if (!tilesRef.current[key]) {
        tilesRef.current[key] = "loading";
        const img = new Image(); img.crossOrigin = "anonymous";
        img.src = `https://tile.openstreetmap.org/${key}.png`;
        img.onload = () => { tilesRef.current[key] = img; setTick(t => t + 1); };
        img.onerror = () => { tilesRef.current[key] = null; };
      } else {
        ctx.fillStyle = C.bgMid; ctx.fillRect(Math.round(px), Math.round(py), 256, 256);
      }
    }}
    ctx.fillStyle = "rgba(6,6,16,0.30)"; ctx.fillRect(0, 0, W, H);

    if (userPos && target) {
      const up = latLonToPixel(userPos.lat, userPos.lon, cLat, cLon, zoom);
      const tp = latLonToPixel(target.lat, target.lon, cLat, cLon, zoom);
      ctx.setLineDash([7, 4]); ctx.strokeStyle = "rgba(239,68,68,0.75)"; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(up.px, up.py); ctx.lineTo(tp.px, tp.py); ctx.stroke();
      ctx.setLineDash([]);
    }
    if (target) {
      const tp = latLonToPixel(target.lat, target.lon, cLat, cLon, zoom);
      ctx.beginPath(); ctx.arc(tp.px, tp.py, 14, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(239,68,68,0.18)"; ctx.fill();
      ctx.beginPath(); ctx.arc(tp.px, tp.py, 6, 0, Math.PI * 2);
      ctx.fillStyle = "#ef4444"; ctx.fill();
      // Croix sur la cible
      ctx.strokeStyle = "#fff"; ctx.lineWidth = 1.5;
      const tp2 = tp;
      ctx.beginPath(); ctx.moveTo(tp2.px - 4, tp2.py - 4); ctx.lineTo(tp2.px + 4, tp2.py + 4); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(tp2.px + 4, tp2.py - 4); ctx.lineTo(tp2.px - 4, tp2.py + 4); ctx.stroke();
    }
    if (userPos) {
      const up = latLonToPixel(userPos.lat, userPos.lon, cLat, cLon, zoom);
      // heading : boussole réelle si dispo, sinon bearing vers la cible, sinon nord (0)
      const personAngle = heading !== null && heading !== undefined
        ? heading
        : target
          ? getBearing(userPos.lat, userPos.lon, target.lat, target.lon)
          : 0;
      drawPerson(ctx, up.px, up.py, personAngle);
    }
    if (!userPos && onMapClick) {
      ctx.fillStyle = "rgba(6,6,16,0.55)"; ctx.fillRect(0, 0, W, H);
      ctx.font = "bold 14px DM Sans,sans-serif"; ctx.fillStyle = C.accent; ctx.textAlign = "center";
      ctx.fillText("Cliquez pour placer votre position", W / 2, H / 2);
    }
  }, [cLat, cLon, userPos, target, tick, onMapClick, zoom, heading]);

  useEffect(() => { draw(); }, [draw]);

  // ── Click pour placer position ────────────────────────────────────────────
  const handleClick = (e) => {
    if (!onMapClick) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = (e.clientX - rect.left) * (W / rect.width);
    const cy = (e.clientY - rect.top) * (H / rect.height);
    const sc = Math.pow(2, zoom) * 256;
    const toX = (lng) => ((lng + 180) / 360) * sc;
    const toY = (la) => { const s = Math.sin((la * Math.PI) / 180); return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * sc; };
    const wx = toX(cLon) + (cx - W / 2), wy = toY(cLat) + (cy - H / 2);
    const clickLon = (wx / sc) * 360 - 180;
    const n = Math.PI - (2 * Math.PI * wy) / sc;
    const clickLat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
    onMapClick({ lat: clickLat, lon: clickLon });
  };

  // ── Pinch-to-zoom mobile ──────────────────────────────────────────────────
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchRef.current = Math.sqrt(dx * dx + dy * dy);
    }
  };
  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const delta = dist - pinchRef.current;
      if (Math.abs(delta) > 12) {
        setZoom(z => Math.max(10, Math.min(17, z + (delta > 0 ? 1 : -1))));
        // Vide le cache de tuiles au changement de zoom
        tilesRef.current = {};
        pinchRef.current = dist;
      }
    }
  };
  const handleTouchEnd = () => { pinchRef.current = null; };

  const changeZoom = (delta) => {
    setZoom(z => Math.max(10, Math.min(17, z + delta)));
    tilesRef.current = {};
  };

  return (
    <div style={{ position: "relative", borderRadius: 14, overflow: "hidden",
      border: `1.5px solid ${C.border}`, boxShadow: C.shadow,
      cursor: onMapClick ? "crosshair" : "default" }}>
      <canvas ref={canvasRef} width={W} height={H} onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ display: "block", width: "100%", height: "auto", touchAction: "none" }} />

      {/* Boutons zoom */}
      <div style={{ position: "absolute", top: 8, right: 8, display: "flex", flexDirection: "column", gap: 4 }}>
        {["+", "−"].map((label, i) => (
          <button key={label} onClick={() => changeZoom(i === 0 ? 1 : -1)} style={{
            width: 30, height: 30, borderRadius: 8, border: `1.5px solid ${C.border}`,
            background: "rgba(13,16,32,0.88)", color: C.text, fontSize: 18, fontWeight: 700,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            lineHeight: 1, backdropFilter: "blur(4px)",
          }}>{label}</button>
        ))}
      </div>

      {/* Niveau de zoom */}
      <div style={{ position: "absolute", bottom: 5, left: 8, fontSize: 9,
        color: C.textLight, background: "rgba(6,6,16,0.7)",
        borderRadius: 4, padding: "2px 6px" }}>z{zoom}</div>
      <div style={{ position: "absolute", bottom: 5, right: 7, fontSize: 9,
        color: C.textLight, background: "rgba(6,6,16,0.7)",
        borderRadius: 4, padding: "2px 6px" }}>© OpenStreetMap</div>
    </div>
  );
}

// ─── Boussole ────────────────────────────────────────────────────────────
function CompassArrow({ bearing, size = 150 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120">
      <defs>
        <radialGradient id="cg" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#1a2240" />
          <stop offset="100%" stopColor="#060610" />
        </radialGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <circle cx="60" cy="60" r="58" fill="url(#cg)" stroke={C.border} strokeWidth="1.5" />
      <circle cx="60" cy="60" r="54" fill="none" stroke={C.blueMid} strokeWidth="0.5" strokeDasharray="2 4" />
      {Array.from({ length: 36 }, (_, i) => i * 10).map(a => {
        const r1 = a % 90 === 0 ? 44 : a % 45 === 0 ? 47 : 50;
        const rad = (a * Math.PI) / 180;
        return (<line key={a}
          x1={60 + r1 * Math.sin(rad)} y1={60 - r1 * Math.cos(rad)}
          x2={60 + 54 * Math.sin(rad)} y2={60 - 54 * Math.cos(rad)}
          stroke={a % 90 === 0 ? C.blue : a % 45 === 0 ? C.blueMid : C.blueLight}
          strokeWidth={a % 90 === 0 ? 1.5 : 0.8} />);
      })}
      {[["N", 0, C.red], ["E", 90, C.textMid], ["S", 180, C.textMid], ["O", 270, C.textMid]].map(([d, a, col]) => (
        <text key={d}
          x={60 + 34 * Math.sin((a * Math.PI) / 180)}
          y={60 - 34 * Math.cos((a * Math.PI) / 180) + 4}
          textAnchor="middle" fill={col} fontSize="9" fontWeight="bold" fontFamily="monospace"
        >{d}</text>
      ))}
      <g transform={`rotate(${bearing},60,60)`} filter="url(#glow)">
        <polygon points="60,11 54,63 60,56 66,63" fill={C.red} />
        <polygon points="60,109 54,57 60,64 66,57" fill={C.textMid} opacity="0.45" />
        <circle cx="60" cy="60" r="5.5" fill={C.red} />
        <circle cx="60" cy="60" r="2.5" fill="#fff" />
      </g>
    </svg>
  );
}

// ─── StatCard ──────────────────────────────────────────────────────────────
function StatCard({ label, value, accent, bg }) {
  return (
    <div style={{ background: bg || C.bgCard, border: `1.5px solid ${C.border}`,
      borderRadius: 12, padding: "12px 14px", flex: 1, minWidth: 0, boxShadow: C.shadow }}>
      <div style={{ fontSize: 9, color: C.textLight, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: accent, fontFamily: "monospace",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
    </div>
  );
}

// ─── Curseur de distance ──────────────────────────────────────────────────
function DistanceSlider({ stepIndex, onChange }) {
  const pct = (stepIndex / (DISTANCE_STEPS.length - 1)) * 100;
  return (
    <div style={{ background: C.bgCard, border: `1.5px solid ${C.border}`,
      borderRadius: 16, padding: "16px 18px", boxShadow: C.shadow }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 9, color: C.textLight, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700 }}>
          Rayon d'aventure
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span style={{ fontSize: 20 }}>{DISTANCE_EMOJIS[stepIndex]}</span>
          <span style={{ fontSize: 16, fontWeight: 800, color: C.blue, fontFamily: "monospace" }}>
            {DISTANCE_LABELS[stepIndex]}
          </span>
        </div>
      </div>

      {/* Curseur */}
      <div style={{ position: "relative", height: 36, display: "flex", alignItems: "center" }}>
        {/* Track fond */}
        <div style={{ position: "absolute", left: 0, right: 0, height: 5,
          background: C.bgSoft, borderRadius: 3, border: `1px solid ${C.border}` }} />
        {/* Track rempli */}
        <div style={{ position: "absolute", left: 0, width: `${pct}%`, height: 5,
          background: `linear-gradient(90deg, ${C.blueDark}, ${C.accent})`,
          borderRadius: 3, transition: "width 0.15s" }} />
        {/* Ticks */}
        {DISTANCE_STEPS.map((_, i) => {
          const p = (i / (DISTANCE_STEPS.length - 1)) * 100;
          return (
            <div key={i} style={{
              position: "absolute",
              left: `${p}%`,
              transform: "translateX(-50%)",
              width: 3, height: 10,
              background: i <= stepIndex ? C.accent : C.border,
              borderRadius: 2,
              top: "50%", marginTop: -5,
              transition: "background 0.15s",
            }} />
          );
        })}
        {/* Input range invisible par-dessus */}
        <input
          type="range"
          min={0}
          max={DISTANCE_STEPS.length - 1}
          step={1}
          value={stepIndex}
          onChange={e => onChange(parseInt(e.target.value))}
          style={{
            position: "absolute", left: 0, right: 0, width: "100%",
            opacity: 0, height: 36, cursor: "pointer", margin: 0, padding: 0,
          }}
        />
        {/* Thumb visible */}
        <div style={{
          position: "absolute",
          left: `${pct}%`,
          transform: "translateX(-50%)",
          width: 22, height: 22,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${C.blue}, ${C.accent})`,
          border: `3px solid ${C.bg}`,
          boxShadow: `0 0 12px rgba(59,130,246,0.5)`,
          transition: "left 0.15s",
          pointerEvents: "none",
        }} />
      </div>

      {/* Labels sous le curseur */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
        {DISTANCE_LABELS.map((label, i) => (
          <span key={i} style={{
            fontSize: 9, color: i === stepIndex ? C.accent : C.textLight,
            fontWeight: i === stepIndex ? 700 : 400,
            transition: "color 0.15s",
            cursor: "pointer",
          }} onClick={() => onChange(i)}>
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Badge PWA offline ────────────────────────────────────────────────────
function OfflineBadge({ isOnline }) {
  if (isOnline) return null;
  return (
    <div style={{
      position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)",
      background: C.bgCard, border: `1.5px solid ${C.borderMid}`,
      borderRadius: 20, padding: "8px 18px", fontSize: 12, color: C.textMid,
      boxShadow: C.shadowMd, zIndex: 100, display: "flex", alignItems: "center", gap: 8,
      animation: "slideUp 0.25s ease",
    }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", display: "inline-block" }} />
      Mode hors-ligne — boussole active, carte en cache
    </div>
  );
}

// ─── App principale ───────────────────────────────────────────────────────
// ─── Persistance localStorage ──────────────────────────────────────────────
const STORAGE_KEY = "randofix_v2_state";
function loadSavedState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    // Vérifie que les données ne sont pas trop vieilles (24h max)
    if (!s.savedAt || Date.now() - s.savedAt > 24 * 60 * 60 * 1000) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return s;
  } catch { return null; }
}

export default function App() {
  const saved = loadSavedState();

  const [userPos, setUserPos] = useState(saved?.userPos ?? null);
  const [cityName, setCityName] = useState(saved?.cityName ?? "");
  const [target, setTarget] = useState(saved?.target ?? null);
  const [pois, setPois] = useState(saved?.pois ?? []);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState(() => {
    // Si l'ancienne session était en GPS live, on revient à "ready" (pas de watchPosition active)
    const p = saved?.phase;
    if (!p || p === "locating") return "pick";
    // Si on avait une position GPS live, on repart en "ready" sans re-watcher
    if (p === "ready" || p === "generated") return p;
    return "pick";
  });
  const [pulse, setPulse] = useState(false);
  const [manualLat, setManualLat] = useState(saved?.manualLat ?? "");
  const [manualLon, setManualLon] = useState(saved?.manualLon ?? "");
  const [inputMode, setInputMode] = useState(saved?.inputMode ?? "gps");
  const [animKey, setAnimKey] = useState(0);
  const [gpsError, setGpsError] = useState(null);
  const [poiError, setPoiError] = useState(null);
  const [poiStatus, setPoiStatus] = useState(null); // null | "fetching" | "cache" | "done"
  const [deviceHeading, setDeviceHeading] = useState(null);
  const [distStepIndex, setDistStepIndex] = useState(saved?.distStepIndex ?? 2); // défaut : 2km
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [sessionRestored, setSessionRestored] = useState(!!saved && (saved.phase === "ready" || saved.phase === "generated"));
  const watchRef = useRef(null);

  // Auto-ferme le toast de restauration après 3s
  useEffect(() => {
    if (!sessionRestored) return;
    const t = setTimeout(() => setSessionRestored(false), 3000);
    return () => clearTimeout(t);
  }, [sessionRestored]);

  // ── Sauvegarde automatique dans localStorage à chaque changement d'état clé ──
  // isFirstRender évite d'écraser le localStorage au montage initial
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    // On ne sauvegarde pas les états transitoires
    if (phase === "locating") return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        userPos,
        cityName,
        target,
        pois,
        phase,
        manualLat,
        manualLon,
        inputMode,
        distStepIndex,
        savedAt: Date.now(),
      }));
    } catch { /* Quota dépassé — on ignore silencieusement */ }
  }, [userPos, cityName, target, pois, phase, manualLat, manualLon, inputMode, distStepIndex]);

  // Suivi connexion
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => { window.removeEventListener("online", handleOnline); window.removeEventListener("offline", handleOffline); };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const h = e.webkitCompassHeading ?? (e.alpha !== null ? 360 - e.alpha : null);
      if (h !== null) setDeviceHeading(h);
    };
    if (window.DeviceOrientationEvent && typeof DeviceOrientationEvent.requestPermission !== "function")
      window.addEventListener("deviceorientation", handler, true);
    return () => window.removeEventListener("deviceorientation", handler, true);
  }, []);

  const requestOrientation = async () => {
    if (typeof DeviceOrientationEvent?.requestPermission === "function") {
      const perm = await DeviceOrientationEvent.requestPermission();
      if (perm === "granted") window.addEventListener("deviceorientation", (e) => {
        const h = e.webkitCompassHeading ?? (e.alpha !== null ? 360 - e.alpha : null);
        if (h !== null) setDeviceHeading(h);
      }, true);
    }
  };

  const bearing = userPos && target ? getBearing(userPos.lat, userPos.lon, target.lat, target.lon) : 0;
  const relBearing = deviceHeading !== null ? (bearing - deviceHeading + 360) % 360 : bearing;
  const distance = userPos && target ? haversineDistance(userPos.lat, userPos.lon, target.lat, target.lon) : 0;
  const targetDist = DISTANCE_STEPS[distStepIndex];

  const locateUser = useCallback(() => {
    setPhase("locating"); setGpsError(null);
    if (!navigator.geolocation) { setGpsError("Géolocalisation non supportée."); setPhase("pick"); return; }
    if (watchRef.current) navigator.geolocation.clearWatch(watchRef.current);
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => { setUserPos({ lat: pos.coords.latitude, lon: pos.coords.longitude }); setCityName("Ma position GPS"); setPhase("ready"); setGpsError(null); },
      (err) => { setGpsError(err.code === 1 ? "Permission refusée. Autorisez la localisation dans votre navigateur." : "Impossible d'obtenir votre position GPS."); setPhase("pick"); },
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
  }, []);

  const stopGPS = () => { if (watchRef.current) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; } };
  const selectCity = (city) => { stopGPS(); setUserPos({ lat: city.lat, lon: city.lon }); setCityName(city.name); setPhase("ready"); setTarget(null); setPois([]); };
  const handleMapClick = ({ lat, lon }) => { setUserPos({ lat, lon }); setCityName(`${lat.toFixed(4)}, ${lon.toFixed(4)}`); setTarget(null); setPois([]); };
  const handleManualSubmit = () => {
    const lat = parseFloat(manualLat), lon = parseFloat(manualLon);
    if (isNaN(lat) || isNaN(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) return;
    stopGPS(); setUserPos({ lat, lon }); setCityName(`${lat.toFixed(4)}, ${lon.toFixed(4)}`); setPhase("ready"); setTarget(null); setPois([]);
  };

  const generateTarget = useCallback(async () => {
    if (!userPos) return;
    setLoading(true); setPulse(true); setTimeout(() => setPulse(false), 500);
    const pt = generateRandomPoint(userPos.lat, userPos.lon, targetDist);
    setTarget(pt); setPois([]); setPoiError(null); setPoiStatus(null); setPhase("generated"); setAnimKey(k => k + 1);
    try {
      const rawElements = await fetchPOIs(userPos, pt, setPoiStatus);
      const elements = rawElements
        .map(n => {
          const elat = n.lat ?? n.center?.lat;
          const elon = n.lon ?? n.center?.lon;
          if (!elat || !elon) return null;
          // Filtre : distance perpendiculaire au trajet ≤ CORRIDOR_RADIUS
          const perpDist = distPointToSegment(elat, elon, userPos.lat, userPos.lon, pt.lat, pt.lon);
          if (perpDist > CORRIDOR_RADIUS) return null;
          return {
            ...n, lat: elat, lon: elon,
            // dist = distance depuis la position utilisateur (pour tri logique du trajet)
            dist: haversineDistance(userPos.lat, userPos.lon, elat, elon),
            perpDist,
            icon: getPOIIcon(n),
            displayName: getPOIName(n),
            label: getPOILabel(n),
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.dist - b.dist) // trié par ordre de rencontre sur le trajet
        .slice(0, 15);
      setPois(elements);
    } catch (e) {
      console.error("[POI] Erreur finale:", e);
      setPoiError(e.message || "Erreur réseau — réessaie dans un moment.");
    }
    setLoading(false);
  }, [userPos, targetDist]);

  const reset = () => {
    stopGPS();
    setPhase("pick"); setTarget(null); setPois([]);
    setUserPos(null); setCityName(""); setGpsError(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  };

  // ─── Styles réutilisables ───────────────────────────────────────────
  const card = { background: C.bgCard, border: `1.5px solid ${C.border}`, borderRadius: 16, boxShadow: C.shadow };
  const tabActive = { background: C.blue, border: `1.5px solid ${C.blue}`, color: "#fff", fontWeight: 700 };
  const tabInactive = { background: C.bgCard, border: `1.5px solid ${C.border}`, color: C.textMid, fontWeight: 600 };
  const inputStyle = {
    width: "100%", background: C.bgSoft, border: `1.5px solid ${C.border}`,
    borderRadius: 10, padding: "11px 14px", color: C.text, fontSize: 14,
    fontFamily: "monospace", outline: "none",
  };
  const btnPrimary = {
    background: `linear-gradient(135deg,${C.blueDark},${C.blue})`, border: "none",
    borderRadius: 14, padding: "16px", color: "#fff", fontSize: 15, fontWeight: 800,
    cursor: "pointer", boxShadow: `0 4px 20px rgba(37,99,235,0.35)`,
    letterSpacing: "-0.02em", transition: "all 0.18s",
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text,
      fontFamily: "'DM Sans','Segoe UI',sans-serif",
      display: "flex", flexDirection: "column", alignItems: "center",
      padding: "0 0 56px", overflowX: "hidden" }}>
      <style>{`
        @keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}
        @keyframes blink{0%,100%{opacity:1}50%{opacity:0.4}}
        @keyframes popIn{from{transform:scale(0.9);opacity:0}to{transform:scale(1);opacity:1}}
        @keyframes slideUp{from{transform:translateY(14px);opacity:0}to{transform:translateY(0);opacity:1}}
        @keyframes pulse{0%,100%{box-shadow:0 0 0 0 rgba(59,130,246,0.4)}50%{box-shadow:0 0 0 8px rgba(59,130,246,0)}}
        *{box-sizing:border-box}
        input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none}
        input[type=range]{-webkit-appearance:none;appearance:none}
        button:active{transform:scale(0.97)!important}
        ::placeholder{color:${C.textLight}}
        select{background:${C.bgSoft};color:${C.text}}
        a{color:${C.accent}}
      `}</style>

      {/* Décor haut */}
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, height: 300, pointerEvents: "none", zIndex: 0,
        background: `radial-gradient(ellipse at 50% -20%, rgba(59,130,246,0.15) 0%, transparent 70%)` }} />

      {/* ─── Header ─── */}
      <div style={{ width: "100%", maxWidth: 480, padding: "28px 20px 0", position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 48, height: 48, borderRadius: 15,
            background: `linear-gradient(135deg,${C.blueDark},${C.accent})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 24, boxShadow: `0 4px 20px rgba(59,130,246,0.4)`, flexShrink: 0 }}>🎲</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 1, color: C.text }}>
              Rando<span style={{ color: C.blue }}>Fix</span>
            </div>
            <div style={{ fontSize: 10, color: C.textLight, letterSpacing: "0.18em", textTransform: "uppercase", marginTop: 2 }}>
              Générateur d'aventure urbaine
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Indicateur online/offline */}
            <div title={isOnline ? "En ligne" : "Hors-ligne"} style={{
              width: 8, height: 8, borderRadius: "50%",
              background: isOnline ? C.green : "#f59e0b",
              boxShadow: `0 0 8px ${isOnline ? C.green : "#f59e0b"}`,
            }} />
            {phase !== "pick" && (
              <button onClick={reset} style={{ ...tabInactive, borderRadius: 10, padding: "7px 13px", fontSize: 11, cursor: "pointer", flexShrink: 0 }}>
                ← Changer
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ width: "100%", maxWidth: 480, padding: "18px 20px 0", position: "relative", zIndex: 1, display: "flex", flexDirection: "column", gap: 14 }}>

        {/* ══ PICK ══ */}
        {phase === "pick" && (
          <div style={{ animation: "slideUp 0.28s ease" }}>
            {/* Tabs */}
            <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
              {[["gps", "📡 GPS"], ["city", "🏙️ Ville"], ["map", "🗺️ Carte"], ["manual", "⌨️ Coords"]].map(([m, l]) => (
                <button key={m} onClick={() => setInputMode(m)} style={{
                  ...(inputMode === m ? tabActive : tabInactive),
                  flex: 1, padding: "9px 0", borderRadius: 10, fontSize: 11, cursor: "pointer", transition: "all 0.15s",
                }}>{l}</button>
              ))}
            </div>

            {/* GPS */}
            {inputMode === "gps" && (
              <div style={{ ...card, padding: 28, textAlign: "center" }}>
                <div style={{ width: 64, height: 64, borderRadius: 20,
                  background: `linear-gradient(135deg,${C.bgMid},${C.accentBg})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 32, margin: "0 auto 16px", border: `2px solid ${C.border}` }}>📡</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginBottom: 8 }}>Utiliser mon GPS réel</div>
                <div style={{ fontSize: 13, color: C.textMid, marginBottom: 24, lineHeight: 1.7 }}>
                  Autorisez la localisation dans votre navigateur.<br />
                  <span style={{ fontSize: 11, color: C.textLight }}>Fonctionne mieux sur mobile ou avec une puce GPS.</span>
                </div>
                <button onClick={() => { locateUser(); requestOrientation(); }} style={{ ...btnPrimary, padding: "13px 28px", fontSize: 14 }}>
                  📍 Activer le GPS
                </button>
                {gpsError && <div style={{ marginTop: 14, color: C.red, fontSize: 12, lineHeight: 1.6, background: C.redBg, borderRadius: 10, padding: "10px 14px" }}>{gpsError}</div>}
              </div>
            )}

            {/* Villes */}
            {inputMode === "city" && (
              <>
                <div style={{ fontSize: 12, color: C.textMid, marginBottom: 10, fontWeight: 600 }}>Choisissez une ville de départ :</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
                  {PRESET_CITIES.map(city => (
                    <button key={city.name} onClick={() => selectCity(city)} style={{
                      ...card, padding: "14px 12px", color: C.text, fontSize: 14, fontWeight: 700,
                      cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: 10,
                      border: `1.5px solid ${C.border}`, transition: "all 0.14s",
                    }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = C.blue; e.currentTarget.style.background = C.bgSoft; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.bgCard; }}
                    >
                      <span style={{ fontSize: 22 }}>{city.emoji}</span>
                      <span>{city.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Carte */}
            {inputMode === "map" && (
              <>
                <div style={{ fontSize: 12, color: C.textMid, marginBottom: 8, fontWeight: 600 }}>Cliquez sur la carte pour placer votre position :</div>
                <MiniMap userPos={userPos} target={null} onMapClick={handleMapClick} heading={deviceHeading} />
                {userPos && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
                    <div style={{ ...card, padding: "10px 14px", fontSize: 12, fontFamily: "monospace", color: C.blue, display: "flex", alignItems: "center", gap: 8 }}>
                      <span>📍</span>{userPos.lat.toFixed(5)}, {userPos.lon.toFixed(5)}
                    </div>
                    <button onClick={() => setPhase("ready")} style={btnPrimary}>✅ Confirmer cette position</button>
                  </div>
                )}
              </>
            )}

            {/* Coords manuelles */}
            {inputMode === "manual" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ fontSize: 12, color: C.textMid, fontWeight: 600 }}>Entrez vos coordonnées GPS :</div>
                {[["Latitude", manualLat, setManualLat, "ex: 48.8566", "-90 à 90"],
                  ["Longitude", manualLon, setManualLon, "ex: 2.3522", "-180 à 180"]].map(([label, val, setter, ph, hint]) => (
                  <div key={label}>
                    <div style={{ fontSize: 11, color: C.textMid, marginBottom: 6, fontWeight: 600 }}>{label} <span style={{ color: C.textLight, fontWeight: 400 }}>({hint})</span></div>
                    <input type="number" value={val} onChange={e => setter(e.target.value)} placeholder={ph} style={inputStyle} />
                  </div>
                ))}
                <button onClick={handleManualSubmit} style={{ ...btnPrimary, marginTop: 4 }}>📍 Utiliser ces coordonnées</button>
              </div>
            )}
          </div>
        )}

        {/* ══ LOCATING ══ */}
        {phase === "locating" && (
          <div style={{ ...card, padding: 36, textAlign: "center", animation: "slideUp 0.25s ease" }}>
            <div style={{ width: 60, height: 60, borderRadius: 18,
              background: `linear-gradient(135deg,${C.bgMid},${C.accentBg})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 28, margin: "0 auto 16px", animation: "pulse 1.5s ease-in-out infinite",
              border: `2px solid ${C.border}` }}>📡</div>
            <div style={{ fontWeight: 800, fontSize: 15, color: C.blue, marginBottom: 8 }}>Acquisition GPS en cours...</div>
            <div style={{ color: C.textMid, fontSize: 12, lineHeight: 1.7 }}>Autorisez la localisation si le navigateur le demande</div>
            <button onClick={() => { stopGPS(); setPhase("pick"); }} style={{
              marginTop: 20, background: C.bgSoft, border: `1.5px solid ${C.border}`,
              borderRadius: 9, padding: "8px 18px", color: C.textMid, fontSize: 12, cursor: "pointer", fontWeight: 600,
            }}>Annuler</button>
          </div>
        )}

        {/* ══ READY / GENERATED ══ */}
        {(phase === "ready" || phase === "generated") && userPos && (
          <>
            {/* Badge position */}
            <div style={{ ...card, padding: "11px 16px", display: "flex", alignItems: "center", gap: 12,
              background: C.accentBg, border: `1.5px solid ${C.blueLight}`, animation: "slideUp 0.22s ease" }}>
              <span style={{ fontSize: 18 }}>📍</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 9, color: C.accent, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700 }}>
                  {cityName || "Position"}
                </div>
                <div style={{ fontSize: 11, fontFamily: "monospace", color: C.textMid, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {userPos.lat.toFixed(5)}, {userPos.lon.toFixed(5)}
                </div>
              </div>
              <div style={{ width: 8, height: 8, borderRadius: "50%",
                background: cityName === "Ma position GPS" ? C.green : C.blue,
                boxShadow: `0 0 10px ${cityName === "Ma position GPS" ? C.green : C.blue}`,
                animation: "blink 2s ease-in-out infinite", flexShrink: 0 }} />
            </div>

            {/* Curseur distance */}
            <DistanceSlider stepIndex={distStepIndex} onChange={setDistStepIndex} />

            {/* Carte */}
            <MiniMap userPos={userPos} target={target} heading={deviceHeading} />

            {/* Bouton générer */}
            <button onClick={generateTarget} disabled={loading} style={{
              ...btnPrimary,
              background: loading ? C.bgSoft : `linear-gradient(135deg,${C.blueDark},${C.blue})`,
              color: loading ? C.textLight : "#fff",
              boxShadow: loading ? "none" : `0 4px 20px rgba(37,99,235,0.35)`,
              cursor: loading ? "not-allowed" : "pointer",
              transform: pulse ? "scale(0.96)" : "scale(1)",
            }}>
              {loading
                ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <span style={{ display: "inline-block", animation: "spin 1s linear infinite" }}>⏳</span>
                    {poiStatus === "cache" ? "Résultats en cache…" : "Recherche des curiosités…"}
                  </span>
                : phase === "generated" ? "🎲 Nouveau point aléatoire" : "🎲 Générer mon aventure !"}
            </button>

            {/* Résultats */}
            {phase === "generated" && target && (
              <>
                {/* Boussole */}
                <div key={animKey} style={{ ...card, padding: "22px 18px",
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
                  animation: "popIn 0.38s cubic-bezier(0.34,1.56,0.64,1)",
                  background: `linear-gradient(160deg,${C.bgCard} 60%,${C.accentBg})`,
                }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 9, color: C.textLight, letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 700 }}>Direction à suivre</div>
                    <div style={{ fontSize: 11, color: C.textMid, marginTop: 4 }}>
                      {deviceHeading !== null ? "🧭 Boussole active — relative à votre orientation" : "Flèche rouge → cap vers le point mystère (nord = haut)"}
                    </div>
                  </div>
                  <CompassArrow bearing={relBearing} size={150} />
                  <div style={{ display: "flex", gap: 10, width: "100%" }}>
                    <StatCard label="Distance" value={formatDist(distance)} accent={C.red} bg={C.redBg} />
                    <StatCard label="Azimut" value={`${Math.round(bearing)}°`} accent={C.blue} bg={C.accentBg} />
                    <StatCard label="Sur le trajet" value={loading ? "…" : pois.length} accent={C.green} bg={C.greenBg} />
                  </div>
                </div>

                {/* Coords cible */}
                <div style={{ ...card, padding: "12px 16px",
                  display: "flex", alignItems: "center", gap: 12,
                  background: C.redBg, border: `1.5px solid rgba(239,68,68,0.2)`,
                  animation: "slideUp 0.3s ease" }}>
                  <span style={{ fontSize: 21 }}>🎯</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 9, color: C.red, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700 }}>Point mystère</div>
                    <div style={{ fontSize: 11, fontFamily: "monospace", color: "#fca5a5", marginTop: 2 }}>
                      {target.lat.toFixed(5)}, {target.lon.toFixed(5)}
                    </div>
                  </div>
                  <a href={`https://maps.google.com/?q=${target.lat},${target.lon}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ background: C.red, border: "none", borderRadius: 9, padding: "7px 13px",
                      color: "#fff", fontSize: 11, fontWeight: 700, textDecoration: "none", flexShrink: 0,
                      boxShadow: "0 2px 8px rgba(239,68,68,0.35)" }}>Maps →</a>
                </div>

                {/* Liste POIs */}
                {!loading && pois.length > 0 && (
                  <div style={{ ...card, overflow: "hidden", animation: "slideUp 0.4s ease" }}>
                    <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`,
                      fontSize: 9, color: C.green, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 700,
                      display: "flex", alignItems: "center", gap: 7, background: C.greenBg }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.green, display: "inline-block" }} />
                      Curiosités naturelles · {pois.length} trouvée{pois.length > 1 ? "s" : ""}
                    </div>
                    {pois.map((poi, i) => (
                      <div key={poi.id} style={{
                        padding: "11px 16px",
                        borderBottom: i < pois.length - 1 ? `1px solid ${C.border}` : "none",
                        display: "flex", alignItems: "center", gap: 12, transition: "background 0.12s", cursor: "default",
                      }}
                        onMouseEnter={e => { e.currentTarget.style.background = C.bgSoft; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                      >
                        <span style={{ fontSize: 22, minWidth: 30, textAlign: "center" }}>{poi.icon}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {poi.displayName || poi.label}
                          </div>
                          {poi.displayName && (
                            <div style={{ fontSize: 10, color: C.textLight, marginTop: 1 }}>{poi.label}</div>
                          )}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                          <span style={{ fontSize: 11, color: C.green, fontFamily: "monospace", fontWeight: 700,
                            background: C.greenBg, borderRadius: 7, padding: "3px 8px", whiteSpace: "nowrap" }}>
                            {formatDist(poi.dist)}
                          </span>
                          <a href={`https://maps.google.com/?q=${poi.lat},${poi.lon}`}
                            target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: 10, color: C.blue, textDecoration: "none", fontWeight: 600 }}>
                            voir →
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!loading && pois.length === 0 && !poiError && (
                  <div style={{ ...card, padding: 20, textAlign: "center", color: C.textLight, fontSize: 13 }}>
                    🌿 Aucune curiosité naturelle répertoriée dans ce secteur — peut-être une vraie terra incognita !
                  </div>
                )}
                {!loading && poiError && (
                  <div style={{ ...card, padding: "14px 16px", background: C.redBg,
                    border: `1.5px solid rgba(239,68,68,0.2)`, borderRadius: 14 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                      <span style={{ fontSize: 20 }}>⚠️</span>
                      <span style={{ color: C.red, fontWeight: 700, fontSize: 13 }}>
                        Impossible de charger les POIs
                      </span>
                    </div>
                    <div style={{ color: C.textMid, fontSize: 12, marginBottom: 12, lineHeight: 1.6 }}>
                      {poiError}
                    </div>
                    <button
                      onClick={() => {
                        setPoiError(null); setPoiStatus(null); setLoading(true);
                        fetchPOIs(userPos, target, setPoiStatus)
                          .then(raw => {
                            const els = raw.map(n => {
                              const elat = n.lat ?? n.center?.lat;
                              const elon = n.lon ?? n.center?.lon;
                              if (!elat || !elon) return null;
                              const perpDist = distPointToSegment(elat, elon, userPos.lat, userPos.lon, target.lat, target.lon);
                              if (perpDist > CORRIDOR_RADIUS) return null;
                              return { ...n, lat: elat, lon: elon, dist: haversineDistance(userPos.lat, userPos.lon, elat, elon), perpDist, icon: getPOIIcon(n), displayName: getPOIName(n), label: getPOILabel(n) };
                            }).filter(Boolean).sort((a,b) => a.dist-b.dist).slice(0,15);
                            setPois(els);
                          })
                          .catch(e => setPoiError(e.message))
                          .finally(() => setLoading(false));
                      }}
                      style={{ background: C.bgMid, border: `1.5px solid ${C.border}`,
                        borderRadius: 9, padding: "8px 16px", color: C.accent,
                        fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                      🔄 Réessayer
                    </button>
                    {!isOnline && (
                      <div style={{ fontSize: 11, color: C.textLight, marginTop: 8 }}>
                        📵 Hors-ligne — seule la boussole est disponible
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <div style={{ marginTop: 28, fontSize: 9, color: C.textLight, position: "relative", zIndex: 1 }}>
        Tiles &amp; POI © OpenStreetMap contributors
      </div>

      {/* Toast session restaurée */}
      {sessionRestored && (
        <div style={{
          position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)",
          background: C.bgCard, border: `1.5px solid ${C.borderMid}`,
          borderRadius: 20, padding: "8px 18px", fontSize: 12, color: C.textMid,
          boxShadow: C.shadowMd, zIndex: 100, display: "flex", alignItems: "center", gap: 10,
          animation: "slideUp 0.25s ease",
          whiteSpace: "nowrap",
        }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.green, display: "inline-block", flexShrink: 0 }} />
          Session restaurée
          <button onClick={() => setSessionRestored(false)} style={{
            background: "none", border: "none", color: C.textLight,
            cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 0 0 4px",
          }}>✕</button>
        </div>
      )}

      <OfflineBadge isOnline={isOnline} />
    </div>
  );
}
